/**
 * AuthGuard - checks for account on boot, syncs with server, and shows auth screens if needed.
 * Handles both invitation-based registration and login for existing accounts.
 *
 * Device approval is non-blocking: pending devices proceed to the app with a banner,
 * and encrypted content that can't be decrypted shows as locked/blurred placeholders.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { getSessionToken, setAccount } from '@yada/storage';
import { isLegacyStrongAnonymousProfile } from '@yada/types';
import { getCurrentAccount, setProfile, setActiveProfileId, getActiveProfileId, getProfilesForCurrentAccount, getProfiles, removeProfile } from '../accounts/accountService';
import { getAccount, getProfiles as getProfilesFromServer, approveDevice } from '@yada/api-client';
import { useAccount } from '../context/AccountContext';
import { DeviceStatusContext, type DeviceStatus } from './DeviceStatusContext';
import { DevicePendingBanner } from './DevicePendingBanner';
import { KeyMismatchBanner } from './KeyMismatchBanner';
import { UnconfirmedEmailBanner } from './UnconfirmedEmailBanner';

const OnboardingWizard = React.lazy(() => import('../onboarding/OnboardingWizard').then(m => ({ default: m.OnboardingWizard })));
const InvitationScreen = React.lazy(() => import('./InvitationScreen').then(m => ({ default: m.InvitationScreen })));
const AcceptInvitationScreen = React.lazy(() => import('./AcceptInvitationScreen').then(m => ({ default: m.AcceptInvitationScreen })));
const LoginScreen = React.lazy(() => import('./LoginScreen').then(m => ({ default: m.LoginScreen })));
const ProfileSetupScreen = React.lazy(() => import('./ProfileSetupScreen').then(m => ({ default: m.ProfileSetupScreen })));
const RecoveryPhraseEntry = React.lazy(() => import('./RecoveryPhraseEntry').then(m => ({ default: m.RecoveryPhraseEntry })));
const DevicePairingScreen = React.lazy(() => import('./DevicePairingScreen').then(m => ({ default: m.DevicePairingScreen })));
import { hydrateAccountSettings, initAccountSettingsSync, persistAccountSettings } from '../accounts/accountSettings';
import { ensurePersonalWorkspace } from '../workspace/workspaceSetup';
import { getCurrentWorkspaceId } from '../workspace/workspaceContext';

/**
 * Try to self-approve the current device.
 * This succeeds when the backend detects no other approved devices (sole device scenario).
 * Returns true if approval succeeded, false otherwise.
 */
async function trySelfApprove(): Promise<boolean> {
  const deviceId = localStorage.getItem('yada:deviceId');
  if (!deviceId) return false;

  try {
    await approveDevice(deviceId);
    console.log('[AuthGuard] Self-approval succeeded (sole device)');
    return true;
  } catch (err) {
    console.log('[AuthGuard] Self-approval failed (multi-device case):', err);
    return false;
  }
}

/**
 * Ensure this device is registered in profile_devices and its public key
 * is uploaded to the server. Both operations are idempotent — safe to call
 * on every startup and after login.
 *
 * Returns whether a key mismatch was detected (local key ≠ server key with
 * existing seeds). Callers should surface a recovery prompt when true.
 */
async function ensureDeviceSetup(): Promise<{ keyMismatch: boolean }> {
  const deviceId = localStorage.getItem('yada:deviceId');
  if (!deviceId) return { keyMismatch: false };

  const [{ registerDevice }, { uploadDevicePublicKey }] = await Promise.all([
    import('@yada/api-client'),
    import('@yada/sync'),
  ]);

  let keyMismatch = false;
  await Promise.all([
    registerDevice(deviceId).catch(err =>
      console.warn('[AuthGuard] Device registration failed:', err)
    ),
    uploadDevicePublicKey().then(status => {
      if (status === 'mismatch') keyMismatch = true;
    }).catch(err =>
      console.warn('[AuthGuard] Device key upload failed:', err)
    ),
  ]);

  return { keyMismatch };
}

const LAST_DEVICE_STATUS_KEY = 'yada:lastKnownDeviceStatus';

/**
 * Check device status and attempt self-approval if pending.
 * Returns the resolved device status and whether a key mismatch was detected.
 */
async function resolveDeviceStatus(isAccountless?: boolean, isTestAccount?: boolean): Promise<{ status: DeviceStatus; keyMismatch: boolean }> {
  if (isAccountless || isTestAccount) {
    localStorage.setItem(LAST_DEVICE_STATUS_KEY, 'approved');
    return { status: 'approved', keyMismatch: false };
  }
  const cachedStatus = (localStorage.getItem(LAST_DEVICE_STATUS_KEY) as DeviceStatus) || 'unknown';

  try {
    // Ensure device is registered and key uploaded before checking status.
    const { keyMismatch } = await ensureDeviceSetup();
    const deviceId = localStorage.getItem('yada:deviceId');
    let serverStatus: 'pending' | 'approved' | 'revoked' = 'approved';
    if (deviceId) {
      try {
        const { getDeviceStatus } = await import('@yada/api-client');
        const res = await getDeviceStatus(deviceId);
        serverStatus = res.status;
      } catch (err) {
        console.warn('[AuthGuard] Failed to fetch device status from server:', err);
      }
    }

    let resolvedStatus: DeviceStatus = 'approved';

    if (serverStatus === 'revoked') {
      resolvedStatus = 'pending';
      localStorage.setItem(LAST_DEVICE_STATUS_KEY, 'pending');
      window.dispatchEvent(new CustomEvent('yada:session-expired'));
    } else if (serverStatus === 'pending') {
      // Device is pending — try self-approval (only succeeds when sole device)
      const approved = await trySelfApprove();
      resolvedStatus = approved ? 'approved' : 'pending';
    }

    let finalKeyMismatch = keyMismatch;

    // When the server reports this device as approved, verify if the device actually has
    // the cryptographic seeds needed to decrypt the user's workspaces.
    if (resolvedStatus === 'approved') {
      try {
        const { getOrCreateDeviceKey } = await import('@yada/crypto');
        const { getWrappedSeeds, listWorkspaces, listDevices } = await import('@yada/api-client');
        const { getLocalWrappedSeeds } = await import('@yada/sync');
        const deviceKey = await getOrCreateDeviceKey();

        const localSeeds = await getLocalWrappedSeeds(undefined, { device_id: deviceKey.deviceId, wrap_type: 'device' });
        if (localSeeds.length === 0) {
          const serverSeeds = await getWrappedSeeds(undefined, { device_id: deviceKey.deviceId, wrap_type: 'device' }).catch(() => []);
          if (serverSeeds.length === 0) {
            // This device has no seeds. Check if workspaces already exist on the server.
            const workspaces = await listWorkspaces().catch(() => []);
            if (workspaces.length > 0) {
              const anySeeds = await getWrappedSeeds(undefined).catch(() => []);
              if (anySeeds.length > 0) {
                // Workspace has existing encrypted content that this device cannot decrypt.
                const devices = await listDevices().catch(() => []);
                const otherApproved = devices.filter((d: any) => d.status === 'approved' && d.id !== deviceKey.deviceId);
                if (otherApproved.length > 0) {
                  console.warn('[AuthGuard] Device approved on server but lacks workspace seeds. Other approved devices exist — initiating pairing.');
                  resolvedStatus = 'pending';
                } else {
                  console.warn('[AuthGuard] Device approved on server but lacks workspace seeds. No other approved devices — recovery required.');
                  finalKeyMismatch = true;
                }
              }
            }
          }
        }
      } catch (seedCheckErr) {
        console.warn('[AuthGuard] Seed availability check skipped:', seedCheckErr);
      }
    }

    localStorage.setItem(LAST_DEVICE_STATUS_KEY, resolvedStatus);
    return { status: resolvedStatus, keyMismatch: finalKeyMismatch };
  } catch (err) {
    console.error('[AuthGuard] Failed to resolve device status:', err);
    // When offline/unreachable, fallback to previously approved/known status rather than locking out the user
    return { status: cachedStatus !== 'unknown' ? cachedStatus : 'approved', keyMismatch: false };
  }
}

/**
 * Fire-and-forget: fetch all workspace seeds for the current device from the
 * auth server, decrypt them, and populate the in-memory cache.
 * Dispatches `yada:workspace:seed-received` so locked CRDTStorage instances
 * unlock without a page reload.
 */
function triggerSeedPrefetch(): void {
  import('@yada/sync')
    .then(({ prefetchWorkspaceSeeds }) => prefetchWorkspaceSeeds())
    .catch(err => console.warn('[AuthGuard] Seed prefetch failed:', err));
}

const PUBLIC_PATHS = ['/manifesto', '/privacy', '/terms', '/auth/callback', '/auth/totp'];

export const AuthGuard = ({ children, onAccountReady }: {
  children: React.ReactNode;
  onAccountReady?: (account: any) => void;
}) => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const [mode, setMode] = useState<'checking' | 'login' | 'register' | 'accept-invite' | 'profile-setup' | 'recovery-entry' | 'onboarding' | 'ready'>('checking');
  const [pendingInviteCode, setPendingInviteCode] = useState<string | null>(null);
  const [account, setAccountState] = useState<any>(null);
  const { initAccount } = useAccount();
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>('unknown');
  const [keyMismatch, setKeyMismatch] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);
  const [showPairingScreen, setShowPairingScreen] = useState(true);
  const hasCheckedRef = useRef(false);
  // Tracks where to return after the recovery phrase flow ('login' → not yet in app; 'ready' → already in app)
  const recoveryReturnMode = useRef<'login' | 'ready'>('login');

  // Offline-first: an expired or unavailable remote auth session pauses online
  // sync and background polling, but never interrupts single-player local mode.
  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn('[AuthGuard] Remote auth session expired — continuing in offline/single-player mode');
      import('@yada/sync')
        .then(({ stopSyncPush, stopSignaling, getUpdatePoller }) => {
          stopSyncPush();
          stopSignaling();
          getUpdatePoller().stop();
        })
        .catch(() => {});
    };
    window.addEventListener('yada:session-expired', handleSessionExpired);
    return () => window.removeEventListener('yada:session-expired', handleSessionExpired);
  }, []);

  useEffect(() => {
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    const syncAccountInBackground = async (token: string, currentAccount: any) => {
      try {
        const [serverAccount, fetchedProfiles] = await Promise.all([
          getAccount(token),
          getProfilesFromServer(token),
        ]);
        let serverProfiles = fetchedProfiles;

        const syncedAccount = {
          ...(currentAccount || {}),
          id: serverAccount.id,
          email: serverAccount.email || currentAccount?.email,
          recoveryMode: serverAccount.recovery_mode,
          securityPrefs: serverAccount.security_prefs,
          enabledApps: serverAccount.enabled_apps,
          pinnedApps: (serverAccount as any).pinned_apps ?? [],
          onboardingComplete: (serverAccount as any).onboarding_complete ?? false,
          serverSynced: true,
          isDeveloper: (serverAccount as any).is_developer ?? false,
          role: (serverAccount as any).role ?? 'user',
          emailVerified: (serverAccount as any).email_verified ?? currentAccount?.emailVerified ?? true,
          createdAt: serverAccount.created_at ? new Date(serverAccount.created_at) : (currentAccount?.createdAt || new Date()),
        } as any;

        if (syncedAccount.email) {
          const { pinOwnerEmail } = await import('../accounts/reclaimSecurity');
          pinOwnerEmail(syncedAccount.email);
        }

        if (serverProfiles.length > 0) {
          const localProfiles = await getProfiles();
          const { planProfileReconciliation, normalizeProfileSyncOrder } = await import('@yada/sync');
          const { remapProfileAppOwnership, cleanupOrphanedAppOwners } = await import('../workspace/appOwnership');
          const { createProfile } = await import('@yada/api-client');

          serverProfiles = normalizeProfileSyncOrder(serverProfiles);
          const plan = planProfileReconciliation(localProfiles, serverProfiles);

          // 1. Re-create any missing local profiles on server if requested
          for (const localToCreate of plan.profilesToCreate) {
            try {
              const created = await createProfile(token, {
                name: localToCreate.name,
                profile_type: localToCreate.profile_type || localToCreate.profileType,
                visibility: localToCreate.visibility,
                metadata: localToCreate.metadata,
              });
              plan.idMapping[localToCreate.id] = created.id;
              serverProfiles.push(created);
            } catch (err) {
              console.warn('[AuthGuard] Failed to re-create local profile on server:', err);
            }
          }
          serverProfiles = normalizeProfileSyncOrder(serverProfiles);

          // 2. Remap app ownership and workspace overrides from local profile IDs to server profile IDs
          remapProfileAppOwnership(plan.idMapping);

          // 3. Remove local profiles that are neither on the server nor mapped
          const serverProfileIds = new Set(serverProfiles.map((p: any) => p.id));
          for (const localProfile of localProfiles) {
            if (!serverProfileIds.has(localProfile.id) && !plan.idMapping[localProfile.id] && !isLegacyStrongAnonymousProfile(localProfile)) {
              await removeProfile(localProfile.id);
            }
          }

          // 4. Save server profiles to local storage
          for (const profileData of serverProfiles) {
            await setProfile({
              id: profileData.id,
              accountId: profileData.account_id || (profileData as any).accountId || syncedAccount.id,
              profileType: ((profileData as any).profile_type || (profileData as any).profileType) as any,
              name: profileData.name,
              verificationLevel: profileData.verification_level ?? (profileData as any).verificationLevel ?? 0,
              visibility: (profileData.visibility || 'public') as any,
              metadata: profileData.metadata || {},
              createdAt: new Date((profileData as any).created_at || (profileData as any).createdAt),
              updatedAt: new Date((profileData as any).updated_at || (profileData as any).updatedAt),
            } as any);
          }

          let activeProfileId = await getActiveProfileId();
          if (activeProfileId && plan.idMapping[activeProfileId]) {
            activeProfileId = plan.idMapping[activeProfileId];
            await setActiveProfileId(activeProfileId);
          } else if (!activeProfileId || !serverProfiles.some((p: any) => p.id === activeProfileId)) {
            const defaultMain = serverProfiles.find((p: any) => (p.profile_type || p.profileType) === 'standard') ||
                                serverProfiles.find((p: any) => (p.profile_type || p.profileType) === 'internal') ||
                                serverProfiles.find((p: any) => (p.profile_type || p.profileType) !== 'household') ||
                                serverProfiles[0];
            activeProfileId = defaultMain.id;
            await setActiveProfileId(activeProfileId);
          }

          // 5. Clean up any remaining orphaned app owners
          cleanupOrphanedAppOwners(serverProfiles.map((p: any) => p.id), activeProfileId);

          const activeProfile = serverProfiles.find((p: any) => p.id === activeProfileId);
          if (activeProfile) {
            hydrateAccountSettings(activeProfile.metadata, activeProfile.id);
          }

          await setAccount(syncedAccount);
          setAccountState(syncedAccount);
          initAccount(syncedAccount);

          window.dispatchEvent(new CustomEvent('yada:profiles:updated'));
          window.dispatchEvent(new CustomEvent('yada:profile:changed', { detail: { profileId: activeProfileId } }));
        } else {
          await setAccount(syncedAccount);
          setAccountState(syncedAccount);
          initAccount(syncedAccount);
        }

        const { status, keyMismatch: km } = await resolveDeviceStatus(syncedAccount.isAccountless, syncedAccount.isTestAccount);
        setDeviceStatus(status);
        setKeyMismatch(km);
        if (status === 'approved') {
          triggerSeedPrefetch();
          const workspaceId = getCurrentWorkspaceId();
          if (workspaceId) {
            import('@yada/sync')
              .then(({ startSyncPush }) => startSyncPush(workspaceId))
              .catch(err => console.warn('[AuthGuard] Failed to start SSE in syncAccountInBackground:', err));
          }
        }

        if (!syncedAccount.isAccountless) {
          initAccountSettingsSync();
          persistAccountSettings().catch(() => {});
        }
        } catch (err: any) {
        if (err?.message?.includes('Device revoked') || err?.message?.includes('Unauthorized') || err?.message?.includes('401') || err?.status === 401) {
          localStorage.setItem(LAST_DEVICE_STATUS_KEY, 'pending');
          setDeviceStatus('pending');
          window.dispatchEvent(new CustomEvent('yada:session-expired'));
        }
        console.debug('[AuthGuard] Background server sync skipped:', err);
      }
    };

    const checkAccount = async () => {
      try {
        const { getAccessToken } = await import('@yada/api-client');
        const token = getAccessToken() || await getSessionToken();
        const currentAccount = await getCurrentAccount();
        const inviteCode = searchParams.get('invite');
        console.log('[AuthGuard] Initial check - account exists:', !!currentAccount, 'isAccountless:', currentAccount?.isAccountless, 'has token:', !!token, 'invite code:', inviteCode);

        // If invite code is present, handle based on whether an account already exists
        if (inviteCode) {
          if (currentAccount) {
            console.log('[AuthGuard] Invite code present, account exists - showing accept-invite screen');
            setPendingInviteCode(inviteCode);
            setMode('accept-invite');
            return;
          } else {
            console.log('[AuthGuard] No account, showing registration screen');
            setMode('register');
            return;
          }
        }

        // If explicit login route is requested, show login screen
        if (pathname === '/login' || pathname === '/signin') {
          console.log('[AuthGuard] Explicit login route requested, showing login screen');
          setMode('login');
          return;
        }

        // If no account exists and no token, show login
        if (!currentAccount && !token) {
          console.log('[AuthGuard] No account and no token, showing login screen');
          setMode('login');
          return;
        }

        // ── Local-First Fast Path ───────────────────────────────────────────
        // If local account exists AND device was already verified as approved, hydrate immediately without waiting for network.
        if (currentAccount) {
          const localProfiles = await getProfilesForCurrentAccount();
          const cachedStatus = (localStorage.getItem(LAST_DEVICE_STATUS_KEY) as DeviceStatus) || null;
          if (localProfiles.length > 0 && cachedStatus === 'approved') {
            console.log('[AuthGuard] Local-first fast boot with', localProfiles.length, 'profiles');
            let activeProfileId = await getActiveProfileId();
            if (!activeProfileId || !localProfiles.some((p: any) => p.id === activeProfileId)) {
              activeProfileId = localProfiles[0].id;
              await setActiveProfileId(activeProfileId);
              window.dispatchEvent(new CustomEvent('yada:profile:changed', { detail: { profileId: activeProfileId } }));
            }
            const activeProfile = localProfiles.find((p: any) => p.id === activeProfileId);
            if (activeProfile) {
              hydrateAccountSettings(activeProfile.metadata, activeProfile.id);
            }

            setAccountState(currentAccount);
            initAccount(currentAccount);

            setDeviceStatus('approved');
            triggerSeedPrefetch();

            await ensurePersonalWorkspace();

            const workspaceId = getCurrentWorkspaceId();
            if (workspaceId) {
              import('@yada/sync')
                .then(({ startSyncPush }) => startSyncPush(workspaceId))
                .catch(err => console.warn('[AuthGuard] Failed to start SSE in local-first boot:', err));
            }

            setMode('ready');
            onAccountReady?.(currentAccount);

            // Sync with server asynchronously in the background (never blocks local UI)
            if (token && typeof navigator !== 'undefined' && navigator.onLine !== false) {
              syncAccountInBackground(token, currentAccount);
            }
            return;
          }
        }

        // ── Fresh Login / No Local Profile Path ──────────────────────────────
        if (token) {
          try {
            const [serverAccount, fetchedProfiles] = await Promise.all([
              getAccount(token),
              getProfilesFromServer(token),
            ]);
            let serverProfiles = fetchedProfiles;
            console.log('[AuthGuard] Syncing fresh account from server - profiles count:', serverProfiles.length);

            const syncedAccount = {
              ...(currentAccount || {}),
              id: serverAccount.id,
              email: serverAccount.email || (currentAccount?.email),
              recoveryMode: serverAccount.recovery_mode,
              securityPrefs: serverAccount.security_prefs,
              enabledApps: serverAccount.enabled_apps,
              pinnedApps: (serverAccount as any).pinned_apps ?? [],
              onboardingComplete: (serverAccount as any).onboarding_complete ?? false,
              serverSynced: true,
              isDeveloper: (serverAccount as any).is_developer ?? false,
              role: (serverAccount as any).role ?? 'user',
              emailVerified: (serverAccount as any).email_verified ?? currentAccount?.emailVerified ?? true,
              createdAt: serverAccount.created_at ? new Date(serverAccount.created_at) : (currentAccount?.createdAt || new Date()),
            } as any;
            if (syncedAccount.email) {
              const { pinOwnerEmail } = await import('../accounts/reclaimSecurity');
              pinOwnerEmail(syncedAccount.email);
            }

            if (serverProfiles.length > 0) {
              const localProfiles = await getProfiles();
              const { planProfileReconciliation, normalizeProfileSyncOrder } = await import('@yada/sync');
              const { remapProfileAppOwnership, cleanupOrphanedAppOwners } = await import('../workspace/appOwnership');
              const { createProfile } = await import('@yada/api-client');

              serverProfiles = normalizeProfileSyncOrder(serverProfiles);
              const plan = planProfileReconciliation(localProfiles, serverProfiles);

              // 1. Re-create any missing local profiles on server if needed
              for (const localToCreate of plan.profilesToCreate) {
                try {
                  const created = await createProfile(token, {
                    name: localToCreate.name,
                    profile_type: localToCreate.profile_type || localToCreate.profileType,
                    visibility: localToCreate.visibility,
                    metadata: localToCreate.metadata,
                  });
                  plan.idMapping[localToCreate.id] = created.id;
                  serverProfiles.push(created);
                } catch (err) {
                  console.warn('[AuthGuard] Failed to re-create local profile on server:', err);
                }
              }
              serverProfiles = normalizeProfileSyncOrder(serverProfiles);

              // 2. Remap app ownership and workspace overrides
              remapProfileAppOwnership(plan.idMapping);

              // 3. Remove local profiles that are neither on the server nor mapped
              const serverProfileIds = new Set(serverProfiles.map((p: any) => p.id));
              for (const localProfile of localProfiles) {
                if (!serverProfileIds.has(localProfile.id) && !plan.idMapping[localProfile.id] && !isLegacyStrongAnonymousProfile(localProfile)) {
                  await removeProfile(localProfile.id);
                }
              }

              for (const profileData of serverProfiles) {
                await setProfile({
                  id: profileData.id,
                  accountId: profileData.account_id || (profileData as any).accountId || syncedAccount.id,
                  profileType: ((profileData as any).profile_type || (profileData as any).profileType) as any,
                  name: profileData.name,
                  verificationLevel: profileData.verification_level ?? (profileData as any).verificationLevel ?? 0,
                  visibility: (profileData.visibility || 'public') as any,
                  metadata: profileData.metadata || {},
                  createdAt: new Date((profileData as any).created_at || (profileData as any).createdAt),
                  updatedAt: new Date((profileData as any).updated_at || (profileData as any).updatedAt),
                } as any);
              }

              let activeProfileId = await getActiveProfileId();
              if (activeProfileId && plan.idMapping[activeProfileId]) {
                activeProfileId = plan.idMapping[activeProfileId];
                await setActiveProfileId(activeProfileId);
              } else if (!activeProfileId || !serverProfiles.some((p: any) => p.id === activeProfileId)) {
                const defaultMain = serverProfiles.find((p: any) => (p.profile_type || p.profileType) === 'standard') ||
                                    serverProfiles.find((p: any) => (p.profile_type || p.profileType) === 'internal') ||
                                    serverProfiles.find((p: any) => (p.profile_type || p.profileType) !== 'household') ||
                                    serverProfiles[0];
                activeProfileId = defaultMain.id;
                await setActiveProfileId(activeProfileId);
              }

              // 4. Clean up any remaining orphaned app owners
              cleanupOrphanedAppOwners(serverProfiles.map((p: any) => p.id), activeProfileId);

              const activeProfile = serverProfiles.find((p: any) => p.id === activeProfileId);
              if (activeProfile) {
                hydrateAccountSettings(activeProfile.metadata, activeProfile.id);
              }

              await setAccount(syncedAccount);
              setAccountState(syncedAccount);
              initAccount(syncedAccount);

              window.dispatchEvent(new CustomEvent('yada:profiles:updated'));
              window.dispatchEvent(new CustomEvent('yada:profile:changed', { detail: { profileId: activeProfileId } }));

              const { status, keyMismatch: km } = await resolveDeviceStatus(syncedAccount.isAccountless, syncedAccount.isTestAccount);
              setDeviceStatus(status);
              setKeyMismatch(km);
              if (status === 'approved') triggerSeedPrefetch();

              if (!syncedAccount.isAccountless) {
                initAccountSettingsSync();
                persistAccountSettings().catch(() => {});
              }

              await ensurePersonalWorkspace();

              if (status === 'approved') {
                const workspaceId = getCurrentWorkspaceId();
                if (workspaceId) {
                  import('@yada/sync')
                    .then(({ startSyncPush }) => startSyncPush(workspaceId))
                    .catch(err => console.warn('[AuthGuard] Failed to start SSE in checkAccount:', err));
                }
              }

              setMode('ready');
              onAccountReady?.(syncedAccount);
              return;
            } else {
              setAccountState(syncedAccount);
              setMode('profile-setup');
              return;
            }
          } catch (err) {
            console.error('Failed to sync account from server:', err);
          }
        }

        if (currentAccount) {
          const localProfiles = await getProfilesForCurrentAccount();
          if (localProfiles.length > 0) {
            let activeProfileId = await getActiveProfileId();
            if (!activeProfileId || !localProfiles.some((p: any) => p.id === activeProfileId)) {
              activeProfileId = localProfiles[0].id;
              await setActiveProfileId(activeProfileId);
              window.dispatchEvent(new CustomEvent('yada:profile:changed', { detail: { profileId: activeProfileId } }));
            }
            const activeProfile = localProfiles.find((p: any) => p.id === activeProfileId);
            if (activeProfile) {
              hydrateAccountSettings(activeProfile.metadata, activeProfile.id);
            }

            setAccountState(currentAccount);
            initAccount(currentAccount);

            const { status: resolvedStatus, keyMismatch: km } = await resolveDeviceStatus(currentAccount.isAccountless, currentAccount.isTestAccount);
            setDeviceStatus(resolvedStatus);
            setKeyMismatch(km);
            if (resolvedStatus === 'approved') triggerSeedPrefetch();

            await ensurePersonalWorkspace();

            setMode('ready');
            onAccountReady?.(currentAccount);
            return;
          }
        }

        // Fallback: show login screen
        setMode('login');
      } catch (err) {
        console.error('[AuthGuard] Failed to check account:', err);
        setMode('login');
      }
    };
    checkAccount();
  }, [onAccountReady, initAccount, pathname, searchParams]);

  const handleLoginSuccess = async (newAccount: any) => {
    setAccountState(newAccount);
    initAccount(newAccount);

    // Check device status (non-blocking)
    const { status, keyMismatch: km } = await resolveDeviceStatus(newAccount.isAccountless, newAccount.isTestAccount);
    setDeviceStatus(status);
    setKeyMismatch(km);
    console.log('[AuthGuard] Device status after login:', status, 'keyMismatch:', km);
    if (status === 'approved') triggerSeedPrefetch();

    // Establish workspace context — same as checkAccount path. Must run before
    // setMode('ready') so apps have a workspace ID when they mount.
    await ensurePersonalWorkspace();

    // Explicitly restart SSE sync. We cannot rely on yada:workspace:activated
    // firing here because clearAllUserData() preserves yada:currentWorkspaceId —
    // re-login to the same workspace causes setCurrentWorkspaceId() to skip the
    // event (no change detected), leaving SSE stopped after the previous logout.
    if (status === 'approved') {
      const workspaceId = getCurrentWorkspaceId();
      if (workspaceId) {
        import('@yada/sync')
          .then(({ startSyncPush }) => startSyncPush(workspaceId))
          .catch(err => console.warn('[AuthGuard] Failed to restart SSE after login:', err));
      }
    }

    if (!newAccount.onboardingComplete) {
      setMode('onboarding');
    } else {
      setMode('ready');
      onAccountReady?.(newAccount);
    }
  };

  const handleRegisterSuccess = async (newAccount: any) => {
    console.log('[AuthGuard] handleRegisterSuccess called with account:', newAccount.id);
    setAccountState(newAccount);
    await new Promise(resolve => setTimeout(resolve, 300));

    const allProfiles = await getProfiles();
    const accountProfiles = allProfiles.filter((p: any) => p.accountId === newAccount.id);
    console.log('[AuthGuard] After registration - all profiles in storage:', allProfiles.length, 'account profiles:', accountProfiles.length, 'accountId:', newAccount.id);

    if (accountProfiles.length === 0) {
      console.warn('[AuthGuard] No profiles found for account after registration, showing profile-setup');
      setMode('profile-setup');
    } else {
      console.log('[AuthGuard] Profiles found:', accountProfiles.map((p: any) => ({ id: p.id, name: p.name })), 'checking device status');

      initAccount(newAccount);

      // Check device status (non-blocking)
      const { status, keyMismatch: km } = await resolveDeviceStatus(newAccount.isAccountless, newAccount.isTestAccount);
      setDeviceStatus(status);
      setKeyMismatch(km);
      console.log('[AuthGuard] Device status after registration:', status, 'keyMismatch:', km);
      if (status === 'approved') triggerSeedPrefetch();

      if (!newAccount.isAccountless) {
        initAccountSettingsSync();
        persistAccountSettings().catch(() => {});
      }

      await ensurePersonalWorkspace();

      if (!newAccount.onboardingComplete) {
        setMode('onboarding');
      } else {
        setMode('ready');
        onAccountReady?.(newAccount);
      }
    }
  };

  const handleProfileSetupComplete = async () => {
    const acct = await getCurrentAccount();
    if (acct) {
      setAccountState(acct);
      initAccount(acct);

      // Check device status (non-blocking)
      const { status, keyMismatch: km } = await resolveDeviceStatus(acct.isAccountless, (acct as any).isTestAccount);
      setDeviceStatus(status);
      setKeyMismatch(km);
      console.log('[AuthGuard] Device status after profile setup:', status, 'keyMismatch:', km);
      if (status === 'approved') triggerSeedPrefetch();

      if (!acct.isAccountless) {
        initAccountSettingsSync();
        persistAccountSettings().catch(() => {});
      }

      console.log('[AuthGuard] Ensuring personal workspace after profile setup (accountless:', acct.isAccountless, ')');
      await ensurePersonalWorkspace();

      if (!acct.onboardingComplete) {
        setMode('onboarding');
      } else {
        setMode('ready');
        onAccountReady?.(acct);
      }
    }
  };

  const handleDeviceApproved = () => {
    localStorage.setItem(LAST_DEVICE_STATUS_KEY, 'approved');
    setDeviceStatus('approved');
    const workspaceId = getCurrentWorkspaceId();
    if (workspaceId) {
      import('@yada/sync')
        .then(({ startSyncPush }) => startSyncPush(workspaceId))
        .catch(err => console.warn('[AuthGuard] Failed to start SSE on device approval:', err));
    }
  };

  const handleUseRecovery = (returnTo: 'login' | 'ready' = 'login') => {
    recoveryReturnMode.current = returnTo;
    setMode('recovery-entry');
  };

  // Legal and other public pages don't require auth — pass straight through.
  // This check must live AFTER all hooks to comply with React's Rules of Hooks.
  if (PUBLIC_PATHS.includes(pathname)) {
    return <>{children}</>;
  }

  if (mode === 'checking') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: 'var(--muted)',
      }}>
        Loading...
      </div>
    );
  }

  const authLoadingFallback = (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      color: 'var(--muted)',
    }}>
      Loading...
    </div>
  );

  if (mode === 'login') {
    return (
      <React.Suspense fallback={authLoadingFallback}>
        <LoginScreen onSuccess={handleLoginSuccess} onSwitchToRegister={() => setMode('register')} onRecover={() => handleUseRecovery('login')} sessionExpiredMessage={sessionExpiredMessage} />
      </React.Suspense>
    );
  }

  if (mode === 'register') {
    return (
      <React.Suspense fallback={authLoadingFallback}>
        <InvitationScreen onSuccess={handleRegisterSuccess} />
      </React.Suspense>
    );
  }

  if (mode === 'accept-invite' && pendingInviteCode) {
    return (
      <React.Suspense fallback={authLoadingFallback}>
        <AcceptInvitationScreen
          inviteCode={pendingInviteCode}
          onSuccess={() => {
            setPendingInviteCode(null);
            setMode('ready');
          }}
          onCancel={() => {
            setPendingInviteCode(null);
            setMode('ready');
          }}
        />
      </React.Suspense>
    );
  }

  if (mode === 'profile-setup') {
    console.log('[AuthGuard] Rendering ProfileSetupScreen');
    return (
      <React.Suspense fallback={authLoadingFallback}>
        <ProfileSetupScreen onComplete={handleProfileSetupComplete} />
      </React.Suspense>
    );
  }

  if (mode === 'recovery-entry') {
    return (
      <React.Suspense fallback={authLoadingFallback}>
        <RecoveryPhraseEntry
          onSuccess={() => {
            setKeyMismatch(false);
            triggerSeedPrefetch();
            localStorage.setItem(LAST_DEVICE_STATUS_KEY, 'approved');
            setDeviceStatus('approved');
            // If we came from inside the app (key mismatch or pending banner),
            // go back to it; otherwise return to login.
            if (recoveryReturnMode.current === 'ready') {
              setMode('ready');
            } else {
              // We successfully recovered, so we can go straight into the app.
              // (Setting it to ready prevents them from falling back to login state)
              setMode('ready');
            }
          }}
          onCancel={() => setMode(recoveryReturnMode.current === 'ready' ? 'ready' : 'login')}
        />
      </React.Suspense>
    );
  }

  if (mode === 'onboarding') {
    return (
      <React.Suspense fallback={authLoadingFallback}>
        <OnboardingWizard
          onComplete={() => {
            setMode('ready');
            onAccountReady?.(account);
          }}
        />
      </React.Suspense>
    );
  }

  if (mode === 'ready') {
    console.log('[AuthGuard] Rendering app (ready mode), deviceStatus:', deviceStatus);
  }

  // Stored seed records do not prove this device can decrypt the required keys.
  // Settings routes are never blocked by the full-screen pairing screen so users can manage storage/devices.
  if (deviceStatus === 'pending' && showPairingScreen && !account?.isTestAccount && !pathname.startsWith('/settings')) {
    return (
      <DeviceStatusContext.Provider value={deviceStatus}>
        <React.Suspense fallback={authLoadingFallback}>
          <DevicePairingScreen
            onLinked={handleDeviceApproved}
            onUseRecovery={() => handleUseRecovery('ready')}
            onSkip={() => setShowPairingScreen(false)}
          />
        </React.Suspense>
      </DeviceStatusContext.Provider>
    );
  }

  return (
    <DeviceStatusContext.Provider value={deviceStatus}>
      {account && account.emailVerified === false && (
        <UnconfirmedEmailBanner email={account.email} />
      )}
      {keyMismatch && deviceStatus !== 'pending' && (
        <KeyMismatchBanner onUseRecovery={() => handleUseRecovery('ready')} />
      )}
      {deviceStatus === 'pending' && (
        <DevicePendingBanner
          onApproved={handleDeviceApproved}
          onUseRecovery={() => handleUseRecovery('ready')}
          onPairNow={() => setShowPairingScreen(true)}
        />
      )}
      {children}
    </DeviceStatusContext.Provider>
  );
};
